import React, { useState, useEffect, useRef, useContext } from 'react';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { v4 as uuidv4 } from 'uuid';
import { ComponentContext } from '../../ContextWrappers/NRComponentContextWrapper';
import { UserContext } from '../../App';
import { useParams } from 'react-router-dom';
import { getToken } from '../../services/auth';
import { useAuth0 } from '@auth0/auth0-react';
import { getDataFromTurbinePPComposedAssetId } from '../../utils/utils';
import { getChatEndpointUrl } from './utils/apiUtils';
import { useSessionId } from './hooks/useSessionId';
import { useChatStream } from './hooks/useChatStream';
import { buildChatPayload } from './utils/messageUtils';
import { processSSEStream } from './utils/sseStreamProcessor';
import DrawerHeader from './components/DrawerHeader';
import WelcomeScreen from './components/WelcomeScreen';
import MessageItem from './components/MessageItem';
import ChatInput from './components/ChatInput';
import NRAIMorphSpinner from '../NRAIMorphSpinner/NRAIMorphSpinner';
import NRAIThinkingButton from '../NRAIThinkingButton/NRAIThinkingButton';
import { getAuthorText, COMPLETION_TEXT } from './authorMapping';

const DRAWER_WIDTH = '950px';

const DrawerMainContainer = Box;
const DrawerContent = Box;

function NRRiseAiChatDrawer({ open, onClose }) {
  const theme = useTheme();
  const { componentId: composedAssetId } = useParams();
  // Extract just the asset ID (without revision) from the composed asset ID
  const assetId = composedAssetId ? getDataFromTurbinePPComposedAssetId(composedAssetId).assetId : null;
  const { asset } = useContext(ComponentContext);
  const { currentOrgId } = useContext(UserContext);
  const { getAccessTokenSilently } = useAuth0();
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [spinners, setSpinners] = useState([]); // Array of spinner states: { id, author, isActive, completed, userMessageIndex, completionText }
  const allEventsRef = useRef([]);
  const messagesEndRef = useRef(null);
  const eventSourceRef = useRef(null);
  const lastProcessedInvocationId = useRef(null);
  const processedChunkIds = useRef(new Set());
  const currentSpinnerIdRef = useRef(null);

  // Use custom hook for session management
  const { sessionId, isLoadingSession, sessionError } = useSessionId(open, assetId);

  // Use custom hook for chat stream event handling
  const { processSSEEvent } = useChatStream({
    setMessages,
    allEventsRef,
    processedChunkIds,
    lastProcessedInvocationId,
    onAuthorChange: (author) => {
      // Update the current active spinner's author (find the most recent active spinner)
      setSpinners(prev => {
        // Find the last active spinner (most recent one that's still active)
        const activeSpinners = prev.filter(s => s.isActive);
        if (activeSpinners.length > 0) {
          const lastActiveSpinner = activeSpinners[activeSpinners.length - 1];
          return prev.map(spinner => 
            spinner.id === lastActiveSpinner.id
              ? { ...spinner, author }
              : spinner
          );
        }
        return prev;
      });
    }
  });

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isStreaming) return;

    // Get JWT token early - prefer Auth0 token, fallback to localStorage
    let jwtToken = null;
    try {
      jwtToken = await getAccessTokenSilently({
        audience: process.env.REACT_APP_AUTH0_AUDIENCE
      });
    } catch (authError) {
      console.warn('Failed to get Auth0 token, falling back to localStorage:', authError);
      jwtToken = getToken();
    }

    if (!jwtToken) {
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: 'Error: JWT token is required. Please ensure you are authenticated.',
          timestamp: new Date(),
          error: true
        }
      ]);
      return;
    }

    // Ensure we have a session ID before sending
    if (!sessionId) {
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: 'Error: Session ID is not available. Please wait for the session to be initialized.',
          timestamp: new Date(),
          error: true
        }
      ]);
      return;
    }

    const userMessage = {
      role: 'user',
      content: inputValue.trim(),
      timestamp: new Date()
    };

    const messageText = inputValue.trim();
    // Get the index where the user message will be added
    const userMessageIndex = messages.length;
    
    // Create a new spinner for this stream, associated with this user message
    const spinnerId = `spinner-${uuidv4()}`;
    currentSpinnerIdRef.current = spinnerId;
    setSpinners(prev => [
      ...prev,
      {
        id: spinnerId,
        author: null,
        isActive: true,
        completed: false,
        userMessageIndex
      }
    ]);
    
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsStreaming(true);
    // Reset tracking for new message
    lastProcessedInvocationId.current = null;
    processedChunkIds.current.clear();

    try {
      // Close any existing connection
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }

      // Build payload
      const payload = buildChatPayload(messageText, sessionId, jwtToken);

      // Create assistant message placeholder
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: '',
          timestamp: new Date()
        }
      ]);

      // Get chat endpoint URL
      const chatUrl = getChatEndpointUrl();

      const response = await fetch(chatUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Authorization': `Bearer ${jwtToken}`,
          'X-Asset-Id': assetId || ''
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(`HTTP error! status: ${response.status}${errorText ? ` - ${errorText}` : ''}`);
      }

      // Store spinner ID in a local variable to ensure it persists through the stream
      const streamSpinnerId = currentSpinnerIdRef.current;
      
      // Process SSE stream
      await processSSEStream(response, {
        processSSEEvent,
        setIsStreaming,
        allEventsRef,
        onStreamStart: () => {
          setIsStreaming(true);
        },
        onStreamEnd: () => {
          setIsStreaming(false);
          // Mark the current spinner as completed using the stored ID
          if (streamSpinnerId) {
            setSpinners(prev => prev.map(spinner => 
              spinner.id === streamSpinnerId
                ? { ...spinner, isActive: false, completed: true, completionText: COMPLETION_TEXT }
                : spinner
            ));
          }
          currentSpinnerIdRef.current = null;
        }
      });
    } catch (error) {
      console.error('Error sending message:', error);

      // Create user-friendly error message
      let errorMessage = 'Sorry, I encountered an error. Please try again.';

      if (error.message) {
        // Check for 429 / rate limit errors
        if (
          error.message.includes('429') ||
          error.message.includes('Resource exhausted') ||
          error.message.includes('RESOURCE_EXHAUSTED') ||
          error.message.includes('rate limit')
        ) {
          errorMessage = 'Rate limit exceeded. The service is currently experiencing high demand. Please wait a moment and try again.';
        } else if (error.message.includes('500')) {
          errorMessage = 'Server error. Please try again in a moment.';
        } else if (error.message.includes('503')) {
          errorMessage = 'Service temporarily unavailable. Please try again later.';
        } else {
          // Extract meaningful error message, removing technical details
          const message = error.message
            .replace(/HTTP error!\s*status:\s*\d+\s*-\s*/i, '')
            .replace(/Error while running agent:\s*/i, '')
            .replace(/failed to call model:\s*/i, '');
          errorMessage = message.length > 200 ? 'An error occurred while processing your request. Please try again.' : message;
        }
      }

      setMessages(prev => {
        const newMessages = [...prev];
        const lastMessage = newMessages[newMessages.length - 1];
        if (lastMessage && lastMessage.role === 'assistant' && !lastMessage.content) {
          return [
            ...newMessages.slice(0, -1),
            {
              role: 'assistant',
              content: errorMessage,
              timestamp: new Date(),
              error: true
            }
          ];
        }
        return newMessages;
      });
      setIsStreaming(false);
      // Mark the current spinner as completed on error
      // Find the most recent active spinner to mark as completed
      setSpinners(prev => {
        const activeSpinners = prev.filter(s => s.isActive);
        if (activeSpinners.length > 0) {
          const lastActiveSpinner = activeSpinners[activeSpinners.length - 1];
          return prev.map(spinner => 
            spinner.id === lastActiveSpinner.id
              ? { ...spinner, isActive: false, completed: true, completionText: COMPLETION_TEXT }
              : spinner
          );
        }
        return prev;
      });
      currentSpinnerIdRef.current = null;
    }
  };

  const handleKeyPress = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handlePromptClick = promptText => {
    setInputValue(promptText);
  };

  return (
    <Drawer
      PaperProps={{ sx: { width: DRAWER_WIDTH } }}
      anchor="right"
      open={open}
      variant="temporary"
      onClose={onClose}
      onKeyDown={event => {
        if (event.key === 'Escape') {
          onClose();
        }
      }}
    >
      <DrawerMainContainer sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <DrawerHeader onClose={onClose} />
          <DrawerContent
            sx={{
              flex: 1,
              overflowY: 'auto',
              padding: theme.spacing(3),
              backgroundColor: theme.palette.background.default,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {messages.length === 0 ? (
              <WelcomeScreen isLoadingSession={isLoadingSession} sessionError={sessionError} onPromptClick={handlePromptClick} />
            ) : (
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Messages with spinners rendered after their corresponding user messages */}
                {messages.map((message, index) => {
                  // Find spinner associated with this user message (if this is a user message)
                  const spinner = message.role === 'user' 
                    ? spinners.find(s => s.userMessageIndex === index)
                    : null;
                  
                  return (
                    <React.Fragment key={index}>
                      <MessageItem message={message} />
                      {/* Render spinner right after user message */}
                      {spinner && (
                        <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                          <NRAIMorphSpinner 
                            key={spinner.id}
                            size={16} 
                            text={
                              spinner.completed && spinner.completionText ? (
                                spinner.completionText
                              ) : (
                                <NRAIThinkingButton
                                  isThinking={!spinner.completed}
                                  label={getAuthorText(spinner.author)}
                                  thinkingLabel={getAuthorText(spinner.author)}
                                  color={theme.palette.text.secondary}
                                  showChevron={false}
                                  asText={true}
                                />
                              )
                            }
                            complete={spinner.completed}
                          />
                        </Box>
                      )}
                    </React.Fragment>
                  );
                })}
                <div ref={messagesEndRef} />
              </Box>
            )}
          </DrawerContent>

          <ChatInput
            inputValue={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onSend={handleSendMessage}
            onKeyPress={handleKeyPress}
            isLoading={isStreaming}
            isLoadingSession={isLoadingSession}
            sessionId={sessionId}
          />
        </DrawerMainContainer>
    </Drawer>
  );
}

export default NRRiseAiChatDrawer;
